import { ShieldCheck, Bot, Users, Send } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";
import Reveal from "@/components/landing/Reveal";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Capitale sul tuo conto broker",
    desc: "I fondi rimangono sul conto personale dell'utente presso il broker utilizzato. TheGoldFx non custodisce direttamente il capitale.",
    accent: "cyan",
    testid: "feature-card-capital",
  },
  {
    icon: Bot,
    title: "Esecuzione automatizzata",
    desc: "Aurum AI analizza il mercato ed esegue automaticamente la strategia secondo parametri prestabiliti.",
    accent: "gold",
    testid: "feature-card-auto",
  },
  {
    icon: Users,
    title: "Configurazione e supporto",
    desc: "Il nostro team ti accompagna nella configurazione tecnica e nell'utilizzo del software.",
    accent: "cyan",
    testid: "feature-card-community",
  },
];

export default function Features() {
  const { openModal } = useLeadModal();

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header — left aligned */}
        <div className="mb-12 max-w-3xl sm:mb-16">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="h-px w-8 bg-gradient-to-r from-yellow-400 to-transparent" />
            <span className="font-heading text-[11px] font-bold uppercase tracking-[0.3em] text-yellow-300">
              Perché scegliere Aurum AI
            </span>
          </div>
          <h2
            data-testid="features-title"
            className="font-heading text-3xl font-black uppercase leading-[1] tracking-tight text-white sm:text-4xl lg:text-5xl"
          >
            Perché <span className="text-gradient-gold">Aurum AI</span>?
          </h2>
        </div>

        {/* 3-column grid */}
        <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-3 lg:gap-7">
          {FEATURES.map((feature, idx) => {
            const Icon = feature.icon;
            const isGold = feature.accent === "gold";
            return (
              <Reveal key={feature.title} delay={idx * 100}>
              <div
                data-testid={feature.testid}
                className={`group relative overflow-hidden rounded-[28px] border bg-[#0a0a0a]/80 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 sm:p-8 ${
                  isGold
                    ? "border-yellow-400/20 hover:border-yellow-400/60"
                    : "border-cyan-400/20 hover:border-cyan-400/60"
                }`}
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10,10,10,0.95) 0%, rgba(20,20,20,0.85) 100%)",
                }}
              >
                <div
                  className={`pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-70 ${
                    isGold ? "bg-yellow-400/30" : "bg-cyan-400/25"
                  }`}
                />

                <div className="mb-6 flex items-start justify-between">
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${
                      isGold
                        ? "border-yellow-400/40 bg-yellow-400/10"
                        : "border-cyan-400/40 bg-cyan-400/10"
                    }`}
                  >
                    <Icon
                      className={`h-7 w-7 ${
                        isGold ? "text-yellow-300" : "text-cyan-300"
                      }`}
                    />
                  </div>
                  <span
                    className={`font-heading text-xs font-black tracking-widest ${
                      isGold ? "text-yellow-400/40" : "text-cyan-400/40"
                    }`}
                  >
                    0{idx + 1}
                  </span>
                </div>

                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">
                  {feature.title}
                </h3>
                <p className="mt-3 font-body text-sm text-gray-400 sm:text-base">
                  {feature.desc}
                </p>

                <div
                  className={`mt-6 h-px w-12 transition-all duration-500 group-hover:w-24 ${
                    isGold
                      ? "bg-gradient-to-r from-yellow-400 to-transparent"
                      : "bg-gradient-to-r from-cyan-400 to-transparent"
                  }`}
                />
              </div>
              </Reveal>
            );
          })}
        </div>

        {/* CTA after the 3 bullets */}
        <div className="mt-12 flex justify-center sm:mt-16">
          <button
            type="button"
            onClick={openModal}
            data-testid="features-cta"
            className="group inline-flex w-full max-w-md items-center justify-center gap-3 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600 px-8 py-5 font-heading text-base font-black uppercase tracking-wider text-black shadow-[0_0_40px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(255,215,0,0.75)] sm:text-lg"
          >
            <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            Scopri di più
          </button>
        </div>
      </div>
    </section>
  );
}
